"use client";

import { useQuery } from "@apollo/client";
import { Card, LiquidPill, PageLoadingStateV2 } from "components/ui/v2";
import { gqlAppreciation } from "gql";
import moment from "moment";
import useTranslation from "next-translate/useTranslation";
import { useState } from "react";
import type { IAppreciationDistribute } from "types/Appreciation/AppreciationDistribute.type";

export interface MoreAppreciationsV2Props {
  userId: string;
  setAppreciationDistributes: (distribute: IAppreciationDistribute) => void;
  selectedAppreciation: IAppreciationDistribute;
  vendorId: string;
}

export function MoreAppreciationsV2({
  userId,
  selectedAppreciation,
  vendorId,
  setAppreciationDistributes,
}: MoreAppreciationsV2Props) {
  const { t } = useTranslation("common");
  const [sortedAppreciations, setSortedAppreciations] = useState<IAppreciationDistribute[]>([]);

  const { data: userAppreciationData, loading: userAppreciationLoading } = useQuery(
    gqlAppreciation.queries.GET_APPREACTIONS_BY_USER,
    {
      skip: !userId,
      variables: { userId, vendorId },
      fetchPolicy: "network-only",
      onCompleted: ({ userAppreciationByVendor }) => {
        const sorted = [...userAppreciationByVendor].sort((a) =>
          a.id === selectedAppreciation.id ? -1 : 0
        );
        setSortedAppreciations(sorted);
      },
    }
  );

  if (sortedAppreciations.length <= 1) return null;

  return (
    <div className="space-y-4 pt-4">
      {userAppreciationLoading ? (
        <PageLoadingStateV2 title={t("Cargando")} description={t("Evaluaciones")} />
      ) : (
        <div className="flex items-center justify-center gap-2">
          <h3 className="text-center text-xl font-semibold text-slate-900 dark:text-white">
            {t("Evaluaciones")}
          </h3>
          <LiquidPill
            as="span"
            variant="indigo"
            appearance="tinted"
            shadow="neutral"
            size="sm"
            text={String(userAppreciationData?.userAppreciationByVendor?.length || 0)}
          />
        </div>
      )}

      <div className="max-h-[260px] space-y-2 overflow-y-auto overflow-x-hidden">
        {sortedAppreciations.map((distribute) => {
          const isSelected = selectedAppreciation?.id === distribute.id;

          return (
            <button
              key={distribute.id}
              type="button"
              className="w-full text-left"
              onClick={() => setAppreciationDistributes(distribute)}
            >
              <Card
                size="full"
                variant="default"
                padding="comfortable"
                shadow={isSelected ? "subtle" : "none"}
                className={`cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-zinc-800/60 ${
                  isSelected
                    ? "ring-2 ring-indigo-400 dark:ring-indigo-500"
                    : ""
                }`}
              >
                <div className="flex flex-col text-left">
                  <span className="font-semibold text-slate-900 dark:text-white">
                    {distribute.appreciation.name}
                  </span>
                  <span className="text-sm text-slate-500 dark:text-zinc-400">
                    {distribute.completedAt
                      ? moment(distribute.completedAt).format("DD/MM/YYYY")
                      : t("Sin fecha de termino")}
                  </span>
                </div>
              </Card>
            </button>
          );
        })}
      </div>
    </div>
  );
}
